import React, { useState } from "react";
import { useDndUsers, updateUserDnd, useDndAuditLog } from "../../util/db";

function AuditLogPanel({ userId }) {
  const { data: logs, status } = useDndAuditLog(userId);

  if (status === "loading") {
    return <p className="text-xs text-gray-400 py-2 px-4">Loading audit log...</p>;
  }

  if (!logs || logs.length === 0) {
    return <p className="text-xs text-gray-400 py-2 px-4">No audit log entries.</p>;
  }

  return (
    <div className="px-4 pb-3">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-gray-400 text-left">
            <th className="py-1 pr-3 font-medium">Time</th>
            <th className="py-1 pr-3 font-medium">Source</th>
            <th className="py-1 pr-3 font-medium">Changes</th>
            <th className="py-1 font-medium">Reason</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {logs.map((log) => (
            <tr key={log.id} className="text-gray-600">
              <td className="py-1 pr-3 whitespace-nowrap">
                {log.timestamp?.toDate
                  ? log.timestamp.toDate().toLocaleString()
                  : "—"}
              </td>
              <td className="py-1 pr-3">
                <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                  {log.sourceSystem}
                </span>
              </td>
              <td className="py-1 pr-3">
                {log.changes && Object.entries(log.changes).map(([field, vals]) => (
                  <span key={field} className="mr-2">
                    {field}: {vals.before ? "ON" : "OFF"} → {vals.after ? "ON" : "OFF"}
                  </span>
                ))}
              </td>
              <td className="py-1 text-gray-400 truncate max-w-[200px]">
                {log.reason || "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function DndManagement() {
  const { data: dndUsers, status } = useDndUsers();
  const [search, setSearch] = useState("");
  const [updating, setUpdating] = useState(null);
  const [expandedUser, setExpandedUser] = useState(null);

  async function handleToggle(user, field) {
    setUpdating(user.id);
    try {
      await updateUserDnd(user.id, {
        dndEmail: field === "dndEmail" ? !user.dndEmail : (user.dndEmail || false),
        dndSms: field === "dndSms" ? !user.dndSms : (user.dndSms || false),
        dndAll: field === "dndAll" ? !user.dndAll : (user.dndAll || false),
      }, "admin");
    } catch (err) {
      alert("Failed to update: " + err.message);
    } finally {
      setUpdating(null);
    }
  }

  async function handleRemoveAll(user) {
    if (!window.confirm(`Remove all DND settings for ${user.email}? They will start receiving emails and SMS again.`)) return;
    setUpdating(user.id);
    try {
      await updateUserDnd(user.id, {
        dndEmail: false,
        dndSms: false,
        dndAll: false,
      }, "admin");
    } catch (err) {
      alert("Failed to update: " + err.message);
    } finally {
      setUpdating(null);
    }
  }

  if (status === "loading") {
    return <p className="text-sm text-gray-500 py-4">Loading DND users...</p>;
  }

  if (status === "error") {
    return <p className="text-sm text-red-500 py-4">Failed to load DND users. Please refresh the page.</p>;
  }

  const users = dndUsers || [];
  const filtered = search
    ? users.filter((u) =>
        (u.email || "").toLowerCase().includes(search.toLowerCase()) ||
        (u.name || "").toLowerCase().includes(search.toLowerCase())
      )
    : users;

  return (
    <div>
      <div className="flex items-center gap-4 mb-4">
        <span className="px-3 py-1 rounded-full bg-red-100 text-red-700 text-sm font-medium">
          {users.length} DND users
        </span>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or email..."
          className="flex-1 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
        />
      </div>

      {filtered.length === 0 ? (
        <p className="text-sm text-gray-500 py-4">
          {users.length === 0 ? "No users have DND enabled." : "No matching users found."}
        </p>
      ) : (
        <div className="rounded-lg border border-gray-200 divide-y divide-gray-100 max-h-[600px] overflow-y-auto">
          {filtered.map((user) => (
            <div key={user.id}>
              <div className="flex items-center justify-between px-4 py-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {user.name || "No name"} <span className="text-gray-400 font-normal">({user.email})</span>
                  </p>
                  <div className="flex gap-2 mt-1">
                    {user.dndAll && <span className="text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-600">All</span>}
                    {user.dndEmail && <span className="text-xs px-1.5 py-0.5 rounded bg-orange-100 text-orange-600">Email</span>}
                    {user.dndSms && <span className="text-xs px-1.5 py-0.5 rounded bg-orange-100 text-orange-600">SMS</span>}
                    {user.dndSource && (
                      <span className="text-xs text-gray-400">via {user.dndSource}</span>
                    )}
                  </div>
                </div>
                <div className="flex gap-1 ml-4">
                  <button
                    onClick={() => setExpandedUser(expandedUser === user.id ? null : user.id)}
                    className="text-xs px-2 py-1 rounded font-medium bg-blue-50 text-blue-600 hover:bg-blue-100"
                    title="View audit log"
                  >
                    Log
                  </button>
                  <button
                    onClick={() => handleToggle(user, "dndAll")}
                    disabled={updating === user.id}
                    className={`text-xs px-2 py-1 rounded font-medium ${
                      user.dndAll ? "bg-red-100 text-red-600" : "bg-gray-100 text-gray-500"
                    } hover:bg-gray-200 disabled:opacity-50`}
                    title="Toggle DND All"
                  >
                    All
                  </button>
                  <button
                    onClick={() => handleToggle(user, "dndEmail")}
                    disabled={updating === user.id}
                    className={`text-xs px-2 py-1 rounded font-medium ${
                      user.dndEmail ? "bg-orange-100 text-orange-600" : "bg-gray-100 text-gray-500"
                    } hover:bg-gray-200 disabled:opacity-50`}
                    title="Toggle DND Email"
                  >
                    Email
                  </button>
                  <button
                    onClick={() => handleToggle(user, "dndSms")}
                    disabled={updating === user.id}
                    className={`text-xs px-2 py-1 rounded font-medium ${
                      user.dndSms ? "bg-orange-100 text-orange-600" : "bg-gray-100 text-gray-500"
                    } hover:bg-gray-200 disabled:opacity-50`}
                    title="Toggle DND SMS"
                  >
                    SMS
                  </button>
                  <button
                    onClick={() => handleRemoveAll(user)}
                    disabled={updating === user.id}
                    className="text-xs px-2 py-1 rounded font-medium text-green-600 bg-green-50 hover:bg-green-100 disabled:opacity-50"
                    title="Remove all DND"
                  >
                    Clear
                  </button>
                </div>
              </div>
              {expandedUser === user.id && (
                <div className="bg-gray-50 border-t border-gray-100">
                  <AuditLogPanel userId={user.id} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
